import { useCallback, useEffect, useMemo, useRef } from 'react';

import type { TChannel } from '@feathr/extender/components/Creatives/types';

import type { ICreativesFilters } from './ProjectCreativesPage.types';
import { getSelectedChannel } from './ProjectCreativesPage.utils';

interface IUseSelectedChannelParams {
  filters: ICreativesFilters;
  setFilters: React.Dispatch<React.SetStateAction<ICreativesFilters>>;
}

interface IUseSelectedChannelReturn {
  /** The currently selected channel derived from the class filter. */
  selectedChannel: TChannel | undefined;
  /** Handler for when dimensions are selected. */
  handleDimensionsChange: (spec: string) => void;
  /** Handler for when dimensions are cleared. */
  handleDimensionsClear: () => void;
}

/**
 * Hook that manages channel selection state and its effects on dimension filtering.
 *
 * Derives the selected channel from the creative class filter and automatically
 * clears the spec filter when the channel changes (since dimensions are channel-specific).
 *
 * @param params - The filters state and setter
 * @returns The selected channel and dimension change handlers
 *
 * @example
 * const { selectedChannel, handleDimensionsChange, handleDimensionsClear } = useSelectedChannel({
 *   filters,
 *   setFilters,
 * });
 */
export function useSelectedChannel({
  filters,
  setFilters,
}: IUseSelectedChannelParams): IUseSelectedChannelReturn {
  const selectedChannel = useMemo(() => getSelectedChannel(filters._cls__in), [filters._cls__in]);

  const prevChannelRef = useRef(selectedChannel);
  useEffect(() => {
    if (prevChannelRef.current !== selectedChannel && filters.spec) {
      setFilters((prev) => ({ ...prev, spec: undefined }));
    }
    prevChannelRef.current = selectedChannel;
  }, [selectedChannel, filters.spec, setFilters]);

  const handleDimensionsChange = useCallback(
    (spec: string) => {
      setFilters((prev) => ({ ...prev, spec }));
    },
    [setFilters],
  );

  const handleDimensionsClear = useCallback(() => {
    setFilters((prev) => ({ ...prev, spec: undefined }));
  }, [setFilters]);

  return {
    selectedChannel,
    handleDimensionsChange,
    handleDimensionsClear,
  };
}
